import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fileUrl, inr } from "@/lib/api";
import { useWishlist } from "@/context/WishlistContext";
import { Seo } from "@/components/site/Seo";
import { TripCard } from "@/components/site/TripCard";
import { BookingDialog } from "@/components/site/BookingDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Heart, Scale, X, Check, Minus } from "lucide-react";

const ROWS = [
  ["Starting price", (t) => <span className="font-heading text-xl text-primary">{inr(t.starting_price)}</span>],
  ["Duration", (t) => t.duration],
  ["Destination", (t) => t.destination],
  ["Category", (t) => <span className="capitalize">{t.category?.replace("-", " ")}</span>],
  ["Next departure", (t) => t.next_departure || "—"],
  ["Departures", (t) => t.departure_count ?? 0],
  ["Rating", (t) => (t.review_count ? `${t.avg_rating} ★ (${t.review_count})` : "New")],
  ["Quad / Triple / Double", (t) => `${inr(t.prices?.quad)} / ${inr(t.prices?.triple)} / ${inr(t.prices?.double)}`],
  ["Highlights", (t) => <ul className="space-y-1">{(t.highlights || []).slice(0, 5).map((h, i) => <li key={i} className="flex gap-1 text-xs"><Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />{h}</li>)}</ul>],
  ["Inclusions", (t) => <ul className="space-y-1">{(t.inclusions || []).slice(0, 6).map((h, i) => <li key={i} className="flex gap-1 text-xs"><Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />{h}</li>)}</ul>],
  ["Exclusions", (t) => <ul className="space-y-1">{(t.exclusions || []).slice(0, 4).map((h, i) => <li key={i} className="flex gap-1 text-xs text-muted-foreground"><Minus className="h-3.5 w-3.5 shrink-0" />{h}</li>)}</ul>],
];

export default function Wishlist() {
  const { ids, compare, toggleCompare, clearCompare, toggle } = useWishlist();
  const [trips, setTrips] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  useEffect(() => { api.get("/trips").then(({ data }) => setTrips(data)); }, []);
  const saved = useMemo(() => trips.filter((t) => ids.includes(t.id)), [trips, ids]);
  const cmp = useMemo(() => trips.filter((t) => compare.includes(t.id)), [trips, compare]);

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-4" data-testid="wishlist-page">
      <Seo title="My Wishlist" description="Trips you've saved on Safarshala — compare and book." noindex />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="text-xs uppercase tracking-[0.2em] text-primary">Saved for later</p><h1 className="font-heading text-4xl sm:text-5xl mt-1">My Wishlist</h1><p className="text-muted-foreground mt-1">{saved.length} trip{saved.length === 1 ? "" : "s"} saved · tick up to 3 to compare side by side</p></div>
        {compare.length > 0 && <Button variant="outline" className="rounded-full" onClick={clearCompare} data-testid="compare-clear"><X className="h-4 w-4 mr-1" /> Clear compare ({compare.length})</Button>}
      </div>

      {!saved.length && (
        <div className="mt-16 rounded-3xl border border-dashed p-12 text-center" data-testid="wishlist-empty">
          <Heart className="h-12 w-12 mx-auto text-muted-foreground" /><p className="font-heading text-2xl mt-4">Nothing saved yet</p><p className="text-muted-foreground text-sm mt-1">Tap the heart on any trip to keep it here.</p>
          <Link to="/trips"><Button className="rounded-full mt-6">Explore trips</Button></Link>
        </div>
      )}

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {saved.map((t, i) => (
          <div key={t.id} className="relative">
            <label className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-full bg-white/95 text-slate-900 px-3 py-1.5 text-xs font-medium shadow cursor-pointer" data-testid="compare-toggle">
              <Checkbox checked={compare.includes(t.id)} onCheckedChange={() => toggleCompare(t.id)} disabled={!compare.includes(t.id) && compare.length >= 3} /> Compare
            </label>
            <TripCard trip={t} index={i} />
          </div>
        ))}
      </div>

      {compare.length >= 2 && !showCompare && (
        <motion.div initial={{ y: 80 }} animate={{ y: 0 }} className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 z-40">
          <Button className="rounded-full h-12 px-6 shadow-2xl" onClick={() => setShowCompare(true)} data-testid="compare-open"><Scale className="h-4 w-4 mr-2" /> Compare {compare.length} trips</Button>
        </motion.div>
      )}

      {showCompare && cmp.length >= 2 && (
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-14" data-testid="compare-table">
          <div className="flex items-center justify-between"><h2 className="font-heading text-3xl">Side by side</h2><Button variant="ghost" onClick={() => setShowCompare(false)}>Hide</Button></div>
          <div className="mt-4 overflow-x-auto rounded-2xl border bg-card">
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="border-b">
                <th className="p-4 text-left w-44 text-muted-foreground font-medium">Trip</th>
                {cmp.map((t) => (
                  <th key={t.id} className="p-4 text-left align-top">
                    <img src={fileUrl(t.cover_image)} alt="" className="h-28 w-full object-cover rounded-xl" />
                    <Link to={`/trips/${t.slug}`} className="font-heading text-xl mt-2 block hover:text-primary">{t.name}</Link>
                    <button type="button" className="text-xs text-muted-foreground hover:text-destructive" onClick={() => toggleCompare(t.id)}>Remove</button>
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {ROWS.map(([label, fn]) => (
                  <tr key={label} className="border-b last:border-0 align-top"><td className="p-4 text-muted-foreground">{label}</td>{cmp.map((t) => <td key={t.id} className="p-4">{fn(t)}</td>)}</tr>
                ))}
                <tr><td className="p-4" />{cmp.map((t) => <td key={t.id} className="p-4"><BookingDialog trip={t}><Button className="w-full rounded-full" data-testid="compare-book">Book Now</Button></BookingDialog></td>)}</tr>
              </tbody>
            </table>
          </div>
        </motion.section>
      )}
    </div>
  );
}
